/**
 * WaitingRoomPage - Student waiting area before session starts
 * Shows joined players, session status, and waits for teacher to start
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SessionStatusResponse, SessionStatus } from '@/types/session';
import { sessionsService } from '@/services/sessions';

export function WaitingRoomPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<SessionStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);

  const loadSession = useCallback(async (playerIdToUse: string) => {
    if (!sessionId) {
      setError('Session ID is missing');
      setLoading(false);
      return;
    }

    try {
      const data = await sessionsService.getSessionStatus(sessionId, playerIdToUse);
      setSession(data);

      if (data.status === SessionStatus.LIVE) {
        navigate(`/student/role/${sessionId}`);
      }

      if (data.status === SessionStatus.COMPLETE) {
        if (data.closureReason === 'AUTO_CLOSED_TIMEOUT') {
          setError('This session was automatically closed after 24 hours. Please contact your teacher.');
        } else {
          setError('This session has already ended.');
        }
      }

      setError(null);
    } catch (err: unknown) {
      console.error('Failed to load session:', err);

      // Detect kick: 403 with "Player not found in this session"
      const statusCode =
        typeof err === 'object' && err !== null && 'statusCode' in err
          ? (err as { statusCode?: number }).statusCode
          : undefined;
      const errorMessage =
        typeof err === 'object' && err !== null && 'message' in err
          ? (err as { message?: string }).message
          : undefined;

      if (statusCode === 403 || (errorMessage && errorMessage.includes('Player not found'))) {
        sessionStorage.removeItem('studentSessionId');
        sessionStorage.removeItem('studentPlayerId');
        setError('You have been removed from this session by the teacher.');
        return;
      }

      const apiMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: unknown }).response === 'object' &&
        (err as { response?: { data?: unknown } }).response?.data &&
        typeof (err as { response?: { data?: { message?: unknown } } }).response?.data?.message === 'string'
          ? (err as { response: { data: { message: string } } }).response.data.message
          : null;
      setError(
        apiMessage ||
          'Failed to load session. The session may have been deleted.'
      );
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    const storedPlayerId = sessionStorage.getItem('studentPlayerId');
    if (!storedPlayerId) {
      navigate('/join');
      return;
    }
    setPlayerId(storedPlayerId);

    loadSession(storedPlayerId);

    const pollInterval = setInterval(() => loadSession(storedPlayerId), 3000);

    return () => clearInterval(pollInterval);
  }, [navigate, loadSession]);

  async function handleLeaveSession() {
    if (confirm('Are you sure you want to leave this session?')) {
      // Fire-and-forget backend removal so navigation still happens on failure
      if (sessionId && playerId) {
        try {
          await sessionsService.removePlayer(sessionId, playerId);
        } catch (err) {
          console.error('Failed to remove player from backend:', err);
        }
      }
      sessionStorage.removeItem('studentSessionId');
      sessionStorage.removeItem('studentPlayerId');
      navigate('/join');
    }
  }

  function getStatusMessage(): string {
    if (!session) return '';

    const joined = session.joinedCount;
    const required = session.requiredPlayers;

    if (joined < required) {
      return `Waiting for ${required - joined} more ${
        required - joined === 1 ? 'player' : 'players'
      } to join...`;
    } else {
      return 'Waiting for teacher to start the session...';
    }
  }

  if (loading && !session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#E2E5EC] via-white to-[#CED4DE] flex items-center justify-center px-4">
        <div className="rounded-2xl border border-[#12356B]/20 bg-white p-10 text-center shadow-xl">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-[#CED4DE] border-t-[#367A25]"></div>
          <p className="font-medium text-slate-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#E2E5EC] via-white to-[#CED4DE] flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-8 text-center shadow-xl">
          <div className="mb-4 text-5xl">⚠</div>
          <h2 className="mb-2 text-2xl font-bold text-[#12356B]">Session Error</h2>
          <p className="mb-6 text-slate-600">{error}</p>
          <button
            onClick={() => navigate('/join')}
            className="rounded-lg bg-[#367A25] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#2d6820]"
          >
            Back to Join Page
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#E2E5EC] via-white to-[#CED4DE] flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-[#12356B]/20 bg-white p-8 text-center shadow-xl">
          <p className="mb-6 text-slate-600">Session not found</p>
          <button
            onClick={() => navigate('/join')}
            className="rounded-lg bg-[#367A25] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#2d6820]"
          >
            Back to Join Page
          </button>
        </div>
      </div>
    );
  }

  const isReady = session.joinedCount >= session.requiredPlayers;
  const missingCount = session.requiredPlayers - session.joinedCount;

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#E2E5EC] via-white to-[#CED4DE] px-4 py-8 sm:px-6">
      <div className="pointer-events-none absolute -left-20 top-20 h-56 w-56 rounded-full bg-[#12356B]/10 blur-2xl" />
      <div className="pointer-events-none absolute -right-16 bottom-12 h-56 w-56 rounded-full bg-[#DD6832]/10 blur-2xl" />

      <div className="relative z-10 mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <img
              src="/icons/centauri-logo.png"
              alt="Centauri"
              className="h-12 sm:h-14"
            />
            <div className="flex items-center gap-2 rounded-lg border border-[#CED4DE] bg-white px-3 py-1.5">
              <span className="text-sm font-medium text-slate-600">Join Code:</span>
              <span className="rounded-md bg-[#E2E5EC] px-2.5 py-1 text-sm font-bold tracking-wider text-[#12356B]">
                {session.code}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.1fr]">
          {/* Status + Instructions */}
          <section className="rounded-2xl border border-[#12356B]/20 bg-white p-6 shadow-xl sm:p-7">
            <div className="mb-5 rounded-xl border border-[#12356B]/25 bg-[#101822] p-4 text-white">
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#DD6832]">
                Team Synchronization
              </div>
              <h2 className="text-2xl font-bold">
                {isReady ? 'Ready to Start' : 'Waiting for Players'}
              </h2>
              <p className="mt-2 text-sm text-slate-300">{getStatusMessage()}</p>
            </div>

            <div className="mb-5 rounded-xl border border-[#CED4DE] bg-[#E2E5EC]/60 p-4 text-center">
              <div className="text-5xl font-bold text-[#12356B]">
                {session.joinedCount} / {session.requiredPlayers}
              </div>
              <div className="mt-1 text-sm font-medium text-slate-600">
                {session.joinedCount === 1 ? 'Player Connected' : 'Players Connected'}
              </div>
            </div>

            <div className="rounded-xl border border-[#CED4DE] bg-white p-4">
              <p className="text-sm text-slate-700">
                {isReady
                  ? 'All players are in. Your teacher can launch the mission at any moment.'
                  : `Share the join code "${session.code}" so ${missingCount} more ${missingCount === 1 ? 'player' : 'players'} can join.`}
              </p>
              <button
                onClick={handleLeaveSession}
                className="mt-4 w-full rounded-lg border-2 border-[#12356B] bg-white px-5 py-2.5 font-semibold text-[#12356B] transition-colors hover:bg-[#E2E5EC]"
              >
                Leave Session
              </button>
            </div>
          </section>

          {/* Players List */}
          <section className="rounded-2xl border border-[#12356B]/20 bg-white p-6 shadow-xl sm:p-7">
            <h3 className="mb-4 text-lg font-bold text-[#12356B]">Players in Session</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {session.players.map((player) => (
                <div
                  key={player.playerId}
                  className={`flex items-center gap-3 rounded-lg border p-3 ${
                    player.playerId === playerId
                      ? 'border-[#12356B] bg-[#E2E5EC]'
                      : 'border-[#CED4DE] bg-white'
                  }`}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#12356B] font-bold text-white">
                    {player.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{player.displayName}</div>
                    {player.playerId === playerId && (
                      <div className="text-xs font-semibold uppercase tracking-wide text-[#DD6832]">
                        You
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {Array.from({
                length: session.requiredPlayers - session.joinedCount,
              }).map((_, index) => (
                <div
                  key={`empty-${index}`}
                  className="flex items-center gap-3 rounded-lg border border-dashed border-[#CED4DE] bg-[#E2E5EC]/40 p-3"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#CED4DE] font-bold text-slate-500">
                    ?
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-500">Waiting...</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="mt-6 rounded-xl border border-[#12356B]/20 bg-[#101822] px-5 py-4 text-center">
          <p className="text-sm text-slate-200">
            {isReady
              ? 'Teacher launch detected soon. Stay on this screen for automatic role assignment.'
              : 'Connection stays live and updates every few seconds while players join.'}
          </p>
        </div>
      </div>
    </div>
  );
}
